// src/ManageStudents.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';
import AddStudent from './AddStudent';
import {
  FaEdit,
  FaMapMarkerAlt,
  FaHistory,
  FaUserCheck,
  FaUserTimes,
  FaPlus,
  FaUser,
  FaKey,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

const studentIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.7/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// collator za leksikografsko poređenje (case/accents insensitive)
const collator = new Intl.Collator(undefined, { sensitivity: 'base', usage: 'sort' });
const compareStudents = (a, b) => {
  const lnA = a?.last_name || '';
  const lnB = b?.last_name || '';
  const cmpLast = collator.compare(lnA, lnB);
  if (cmpLast !== 0) return cmpLast;
  const fnA = a?.first_name || '';
  const fnB = b?.first_name || '';
  return collator.compare(fnA, fnB);
};

// Discipline badge-ovi (EI / PI / ABA / OT / PT)
function DisciplineBadges({ disciplines }) {
  if (!Array.isArray(disciplines) || disciplines.length === 0) return null;

  const COLOR = {
    EI: 'bg-blue-100 text-blue-800',
    PI: 'bg-orange-100 text-orange-800',
    ABA: 'bg-purple-100 text-purple-800',
    OT: 'bg-teal-100 text-teal-800',
    PT: 'bg-pink-100 text-pink-800',
	ST: 'bg-green-100 text-green-800',
  };

  const TITLE = {
    EI: 'Early Intervention (EI)',
    PI: 'Project Impact (PI)',
    ABA: 'Applied Behavior Analysis (ABA)',
    OT: 'Occupational Therapy (OT)',
    PT: 'Physical Therapy (PT)',
	ST: 'Speech Therapy (ST)',
  };

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {disciplines.map((d, idx) => {
        const st = (d.service_type || '').toUpperCase();
        const cls = COLOR[st] || 'bg-gray-100 text-gray-700';
        const title = TITLE[st] || st;
        return (
          <span
            key={d.discipline_id || `${st}-${idx}`}
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1 ${cls}`}
            title={title}
          >
            <span>{st}</span>
            {d.is_primary ? (
              <span className="text-[9px] uppercase bg-white/60 px-1 rounded-full">
                primary
              </span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

const ManageStudents = () => {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');

  // filter: all | active | inactive (kopirano iz ManageTeachers dizajna)
  const [filterMode, setFilterMode] = useState('active');

  const [modalOpen, setModalOpen] = useState({ open: false, student: null });
  const [auditLog, setAuditLog] = useState([]);
  const [activeStudentForAudit, setActiveStudentForAudit] = useState(null);
  const [mapModal, setMapModal] = useState({
    open: false,
    lat: null,
    lon: null,
    name: '',
    address: '',
  });

  // CSV import modal state
  const [importErrors, setImportErrors] = useState([]);
  const [showImportErrorModal, setShowImportErrorModal] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(null);
  const [importFailCount, setImportFailCount] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);

  // Parent magic-login modal state
  const [parentLoginModalOpen, setParentLoginModalOpen] = useState(false);
  const [selectedStudentForParentLogin, setSelectedStudentForParentLogin] = useState(null);
  const [sendingParentId, setSendingParentId] = useState(null);

  const fetchStudents = async () => {
    const params = {};
    if (selectedTeacher && selectedTeacher !== '') params.teacher_id = selectedTeacher;

    try {
      const res = await API.get('/students/all', { params });
      setStudents(res.data || []);
    } catch (err) {
      toast.error('Error loading students');
      setStudents([]);
    }
  };

  const fetchTeachers = async () => {
    try {
      const res = await API.get('/teachers/planner');
      setTeachers(res.data || []);
    } catch (err) {
      setTeachers([]);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeacher]);

  // --- Filter UI klase (identično ManageTeachers) ---
  const filterBtnBase =
    'px-2 py-1 rounded-full text-[11px] md:text-xs border transition-colors';
  const activeFilterClasses = 'bg-sky-700 text-white border-sky-700 shadow-sm';
  const inactiveFilterClasses =
    'bg-white text-slate-600 border-slate-300 hover:bg-slate-100';

  // filtrirana + sortirana lista (A–Z)
  const filteredSortedStudents = useMemo(() => {
    const arr = Array.isArray(students) ? students : [];
    const filtered = arr.filter((s) => {
      if (filterMode === 'active') return !!s.is_active;
      if (filterMode === 'inactive') return !s.is_active;
      return true; // all
    });
    return [...filtered].sort(compareStudents);
  }, [students, filterMode]);

  // History
  const loadAuditLog = async (studentId) => {
    try {
      const res = await API.get(`/students/${studentId}/audit`);
      setAuditLog(res.data || []);
      setActiveStudentForAudit(studentId);
    } catch (err) {
      setAuditLog([]);
      toast.error('Error loading change history');
    }
  };

  const openEditModal = (student) => {
    setAuditLog([]);
    setActiveStudentForAudit(null);
    setModalOpen({ open: true, student });
  };

  // Toggle Active
  const toggleActiveStatus = async (student) => {
    try {
      await API.put(`/students/${student.id}/status`, {
        is_active: !student.is_active,
      });
      fetchStudents();
      toast.success('Status updated!');
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  // Map (primarna i ALT lokacija)
  const showStudentLocation = (student, useAlt = false) => {
    const lat = useAlt ? student.alt_latitude : student.latitude;
    const lon = useAlt ? student.alt_longitude : student.longitude;

    if (lat && lon) {
      const addressParts = useAlt
        ? [
            student.alt_address_street,
            student.alt_address_city,
            student.alt_address_zip,
            student.alt_address_country,
          ]
        : [
            student.address_street,
            student.address_city,
            student.address_zip,
            student.address_country,
          ];

      setMapModal({
        open: true,
        lat,
        lon,
        name: `${student.last_name || ''}${student.last_name ? ', ' : ''}${
          student.first_name || ''
        }`,
        address: addressParts.filter(Boolean).join(', '),
      });
    } else {
      toast.info(useAlt ? 'No ALT location data' : 'No location data');
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const res = await API.get('/students/export');
      const csv = Papa.unparse(res.data || []);
      const blob = new Blob([csv], { type: 'text/csv' });
      saveAs(blob, 'students_export.csv');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  // BATCH IMPORT CSV (sa loading i progress barom)
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.endsWith('.csv')) {
      toast.error('Only CSV files are supported!');
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async function (results) {
        setImportLoading(true);
        setImportProgress(0);
        setImportTotal(results.data.length);
        try {
          let success = 0;
          let failed = 0;
          let allResults = [];
          for (let i = 0; i < results.data.length; i++) {
            setImportProgress(i + 1);
            try {
              const res = await API.post('/students/import-csv', results.data[i]);
              const importResult = Array.isArray(res.data?.results)
                ? res.data.results[0]
                : res.data;
              if (importResult && importResult.success) success++;
              else failed++;
              allResults.push(
                importResult || {
                  row: results.data[i],
                  success: false,
                  error: 'Unknown error',
                }
              );
            } catch (err) {
              failed++;
              allResults.push({
                row: results.data[i],
                success: false,
                error: err.response?.data?.error || err.message,
              });
            }
          }
          setImportSuccessCount(success);
          setImportFailCount(failed);
          const errors = allResults.filter((r) => !r.success);
          if (errors.length > 0) {
            setImportErrors(errors);
            setShowImportErrorModal(true);
          } else {
            setImportErrors([]);
            setShowImportErrorModal(false);
          }
          toast.success(`Imported: ${success}, Failed: ${failed}`);
          fetchStudents();
        } catch (err) {
          toast.error('Import failed: ' + (err.response?.data?.error || err.message));
        }
        setImportLoading(false);
        setImportProgress(0);
        setImportTotal(0);
      },
    });
  };

  // ---------- Parent magic-link helpers ----------

  const openParentLoginModal = (student) => {
    const parentsArr = Array.isArray(student.parents) ? student.parents : [];
    if (!parentsArr.length) {
      toast.info('No parents found for this student.');
      return;
    }
    const parentsWithEmail = parentsArr.filter((p) => p.email);
    if (!parentsWithEmail.length) {
      toast.error('No parent emails available for this student.');
      return;
    }
    setSelectedStudentForParentLogin(student);
    setParentLoginModalOpen(true);
  };

  const handleSendParentLoginLink = async (parent) => {
    if (!parent?.id) {
      toast.error('Parent ID missing (save student & parents first).');
      return;
    }
    if (!parent.email) {
      toast.error('Parent has no email.');
      return;
    }

    try {
      setSendingParentId(parent.id);
      await API.post(`/parents/${parent.id}/send-login-link`);
      toast.success(`Login link & QR sent to ${parent.email}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send login link');
    } finally {
      setSendingParentId(null);
    }
  };

  const closeParentLoginModal = () => {
    setParentLoginModalOpen(false);
    setSelectedStudentForParentLogin(null);
    setSendingParentId(null);
  };

  // MOBILE CARDS
  const renderMobileCards = () => (
    <div className="flex flex-col gap-3 md:hidden">
      {filteredSortedStudents.map((s, index) => {
        const age = s.dob
          ? Math.floor(
              (new Date() - new Date(s.dob)) / (365.25 * 24 * 60 * 60 * 1000)
            )
          : '';
        const parents = s.parents?.map((p) => `${p.first_name} ${p.last_name}`).join(', ');
        const addressString =
          [s.address_street, s.address_city, s.address_zip, s.address_country]
            .filter(Boolean)
            .join(', ') || '-';
        const addressAltString =
          [s.alt_address_street, s.alt_address_city, s.alt_address_zip, s.alt_address_country]
            .filter(Boolean)
            .join(', ') || 'ALT address';
        const muted = !s.is_active ? 'bg-gray-50 text-gray-600' : 'bg-white';
        const hasAltAddr =
          s.alt_address_street || s.alt_address_city || s.alt_address_zip || s.alt_address_country;

        const hasParentWithEmail =
          Array.isArray(s.parents) && s.parents.some((p) => p.email && p.email.trim() !== '');

        return (
          <div key={s.id} className={`${muted} rounded shadow p-3 flex flex-col`}>
            <div className="flex justify-between items-start">
              <div>
                {/* Redni broj + „Prezime, Ime“ */}
                <span className="font-semibold">
                  {index + 1}. {s.last_name}
                  {s.last_name ? ', ' : ''}
                  {s.first_name}
                </span>
                <DisciplineBadges disciplines={s.disciplines} />
                <div className="text-xs">
                  {s.gender?.toUpperCase()} | DOB: {s.dob?.slice(0, 10)} | Age: {age}
                </div>
                <div className="text-xs">UCI: {s.uci || '-'}</div>
                <div className="flex gap-2 mt-1">
                  <span className="text-xs font-medium">Provider:</span>
                  <span className="text-xs">{s.teacher_name}</span>
                </div>
                <div className="text-xs mt-1">Parents: {parents}</div>
                <div className="text-xs mt-1">Ref Agent: {s.referring_agent || '-'}</div>
              </div>

              <div className="flex flex-col gap-2 items-center">
                <span title={s.is_active ? 'Active' : 'Inactive'}>
                  <FaUser
                    className={`${s.is_active ? 'text-green-600' : 'text-red-600'} text-lg`}
                  />
                </span>

                <button onClick={() => openEditModal(s)} className="text-blue-600" title="Edit">
                  <FaEdit />
                </button>

                <button onClick={() => toggleActiveStatus(s)} title="Toggle Active">
                  {s.is_active ? (
                    <FaUserTimes className="text-red-600" />
                  ) : (
                    <FaUserCheck className="text-green-600" />
                  )}
                </button>

                <button onClick={() => showStudentLocation(s)} title={addressString}>
                  <FaMapMarkerAlt
                    className={s.latitude && s.longitude ? 'text-blue-600' : 'text-gray-300'}
                  />
                </button>

                {hasAltAddr && (
                  <button
                    onClick={() => showStudentLocation(s, true)}
                    title={addressAltString}
                    disabled={!s.alt_latitude || !s.alt_longitude}
                    className={!s.alt_latitude || !s.alt_longitude ? 'opacity-30 cursor-not-allowed' : ''}
                  >
                    <FaMapMarkerAlt
                      className={
                        s.alt_latitude && s.alt_longitude ? 'text-purple-600' : 'text-gray-300'
                      }
                    />
                  </button>
                )}

                <button
                  onClick={() => openParentLoginModal(s)}
                  title="Send parent login link"
                  disabled={!hasParentWithEmail}
                  className={
                    hasParentWithEmail ? 'text-amber-600' : 'text-gray-300 cursor-not-allowed'
                  }
                >
                  <FaKey />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // DESKTOP TABLE
  const renderDesktopTable = () => (
    <div className="hidden md:block overflow-x-auto">
      <table className="min-w-full border text-xs md:text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="border p-2">Name</th>
            <th className="border p-2">DOB</th>
            <th className="border p-2">Age</th>
            <th className="border p-2">Gender</th>
            <th className="border p-2">Provider</th>
            <th className="border p-2">Disciplines</th>
            <th className="border p-2">Parents</th>
            <th className="border p-2">UCI</th>
            <th className="border p-2">Referring Agent</th>
            <th className="border p-2 text-center">Address</th>
            <th className="border p-2 text-center">Active</th>
            <th className="border p-2 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredSortedStudents.map((s, index) => {
            const age = s.dob
              ? Math.floor(
                  (new Date() - new Date(s.dob)) / (365.25 * 24 * 60 * 60 * 1000)
                )
              : '';
            const parents = s.parents?.map((p) => `${p.first_name} ${p.last_name}`).join(', ');
            const addressString =
              [s.address_street, s.address_city, s.address_zip, s.address_country]
                .filter(Boolean)
                .join(', ') || '-';
            const addressAltString =
              [s.alt_address_street, s.alt_address_city, s.alt_address_zip, s.alt_address_country]
                .filter(Boolean)
                .join(', ') || 'ALT address';
            const rowMuted = !s.is_active ? 'bg-gray-50 text-gray-600' : '';
            const hasAltAddr =
              s.alt_address_street || s.alt_address_city || s.alt_address_zip || s.alt_address_country;
            const hasParentWithEmail =
              Array.isArray(s.parents) && s.parents.some((p) => p.email && p.email.trim() !== '');

            return (
              <tr key={s.id} className={rowMuted}>
                <td className="border p-2 whitespace-nowrap">
                  {index + 1}. {s.last_name}
                  {s.last_name ? ', ' : ''}
                  {s.first_name}
                </td>
                <td className="border p-2">{s.dob?.slice(0, 10)}</td>
                <td className="border p-2">{age}</td>
                <td className="border p-2 capitalize">{s.gender}</td>
                <td className="border p-2">{s.teacher_name}</td>
                <td className="border p-2">
                  <DisciplineBadges disciplines={s.disciplines} />
                </td>
                <td className="border p-2 text-xs">{parents}</td>
                <td className="border p-2">{s.uci || '-'}</td>
                <td className="border p-2">{s.referring_agent || '-'}</td>

                {/* ADDRESS pins — SIDE BY SIDE */}
                <td className="border p-2 text-center whitespace-nowrap">
                  <div className="inline-flex items-center justify-center gap-2 align-middle">
                    <button
                      type="button"
                      title={addressString}
                      className={`p-1 rounded-full ${
                        s.latitude && s.longitude
                          ? 'text-blue-600 hover:text-blue-800'
                          : 'opacity-30 cursor-not-allowed text-gray-300'
                      }`}
                      disabled={!s.latitude || !s.longitude}
                      onClick={() => showStudentLocation(s)}
                    >
                      <FaMapMarkerAlt size={18} />
                    </button>

                    {hasAltAddr && (
                      <button
                        type="button"
                        title={addressAltString}
                        className={`p-1 rounded-full ${
                          s.alt_latitude && s.alt_longitude
                            ? 'text-purple-600 hover:text-purple-800'
                            : 'opacity-30 cursor-not-allowed text-gray-300'
                        }`}
                        disabled={!s.alt_latitude || !s.alt_longitude}
                        onClick={() => showStudentLocation(s, true)}
                      >
                        <FaMapMarkerAlt size={18} />
                      </button>
                    )}
                  </div>
                </td>

                <td className="border p-2 text-center">
                  <FaUser
                    className={`${s.is_active ? 'text-green-600' : 'text-red-600'} inline`}
                  />
                </td>

                <td className="border p-2 text-center space-x-3 whitespace-nowrap">
                  <button
                    onClick={() => openEditModal(s)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => toggleActiveStatus(s)}
                    className={`${
                      s.is_active
                        ? 'text-red-600 hover:text-red-700'
                        : 'text-green-600 hover:text-green-700'
                    }`}
                    title="Toggle Active"
                  >
                    {s.is_active ? <FaUserTimes /> : <FaUserCheck />}
                  </button>
                  <button
                    onClick={() => loadAuditLog(s.id)}
                    className="text-gray-700 hover:text-gray-900"
                    title="History"
                  >
                    <FaHistory />
                  </button>
                  <button
                    onClick={() => openParentLoginModal(s)}
                    className={`${
                      hasParentWithEmail
                        ? 'text-amber-600 hover:text-amber-700'
                        : 'text-gray-300 cursor-not-allowed'
                    }`}
                    disabled={!hasParentWithEmail}
                    title={
                      hasParentWithEmail
                        ? 'Send parent login link'
                        : 'No parent email available'
                    }
                  >
                    <FaKey />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-1">
      <ToastContainer position="top-center" />

      {/* Header (identično kao ManageTeachers) */}
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <h2 className="text-lg font-semibold">Manage Students</h2>

        <div className="flex flex-col gap-2 items-stretch md:flex-row md:items-center md:gap-3">
          {/* Filter buttons (pill group) */}
          <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-full px-1 py-0.5 self-start md:self-auto">
            <button
              className={
                filterBtnBase +
                ' ' +
                (filterMode === 'all' ? activeFilterClasses : inactiveFilterClasses)
              }
              onClick={() => setFilterMode('all')}
              type="button"
            >
              All
            </button>
            <button
              className={
                filterBtnBase +
                ' ' +
                (filterMode === 'active' ? activeFilterClasses : inactiveFilterClasses)
              }
              onClick={() => setFilterMode('active')}
              type="button"
            >
              Active
            </button>
            <button
              className={
                filterBtnBase +
                ' ' +
                (filterMode === 'inactive' ? activeFilterClasses : inactiveFilterClasses)
              }
              onClick={() => setFilterMode('inactive')}
              type="button"
            >
              Inactive
            </button>
          </div>

          {/* Provider select + Export/Import/Add */}
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-2">
            {/* ✅ NEW: provider select in "pill/capsule" */}
            <div className="bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center w-full md:w-auto">
              <select
                value={selectedTeacher}
                onChange={(e) => setSelectedTeacher(e.target.value)}
                className="bg-transparent border-0 outline-none focus:ring-0 text-xs py-1 w-full md:w-auto"
              >
                <option value="">All providers</option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCSV}
                className="bg-green-600 text-white px-2 py-1 rounded text-xs"
              >
                Export CSV
              </button>
              <label className="bg-sky-600 text-white px-2 py-1 rounded cursor-pointer text-xs">
                Import CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleImportCSV}
                  className="hidden"
                />
              </label>
              <button
                onClick={() => openEditModal(null)}
                className="bg-sky-700 text-white px-2 py-1 rounded flex items-center gap-1 text-xs"
              >
                <FaPlus /> Add
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* LOADING + PROGRESS BAR */}
      {importLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 min-w-[280px] flex flex-col items-center">
            <span className="text-lg font-semibold mb-2">Importing data...</span>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div
                className="bg-blue-500 h-4 rounded-full transition-all"
                style={{
                  width: importTotal
                    ? `${Math.round((importProgress / importTotal) * 100)}%`
                    : '0%',
                  transition: 'width 0.2s',
                }}
              />
            </div>
            <span className="text-sm text-gray-700">
              {importProgress} / {importTotal}
            </span>
            <span className="text-xs text-gray-400 mt-2">
              This may take a while due to address geocoding...
            </span>
          </div>
        </div>
      )}

      {renderMobileCards()}
      {renderDesktopTable()}

      {/* Add/Edit Modal */}
      {modalOpen.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white maxh-[95vh] max-h-[95vh] overflow-y-auto p-4 rounded shadow w-[98%] max-w-3xl">
            <AddStudent
              student={modalOpen.student}
              onSaved={() => {
                setModalOpen({ open: false, student: null });
                fetchStudents();
              }}
              onCancel={() => setModalOpen({ open: false, student: null })}
            />
          </div>
        </div>
      )}

      {/* Import Error Modal */}
      {showImportErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg mb-4 text-red-700">Import Errors</h3>
            <div className="mb-3">
              <span className="text-green-700 font-bold mr-4">
                Imported: {importSuccessCount}
              </span>
              <span className="text-red-700 font-bold">Failed: {importFailCount}</span>
            </div>
            <ul className="text-xs md:text-sm space-y-2 max-h-80 overflow-y-auto">
              {importErrors.map((err, i) => (
                <li key={i} className="border-b pb-1 mb-1">
                  <span className="font-semibold text-red-600">Error:</span> {err.error}
                  <br />
                  <span className="text-gray-700">{JSON.stringify(err.row)}</span>
                </li>
              ))}
            </ul>
            <button
              className="mt-4 bg-gray-300 px-4 py-2 rounded"
              onClick={() => setShowImportErrorModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Audit log modal */}
      {activeStudentForAudit && !modalOpen.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow w-[90%] max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FaHistory /> Change History
            </h3>
            <ul className="text-sm space-y-2 max-h-64 overflow-y-auto pr-2">
              {auditLog.length === 0 ? (
                <li className="text-gray-500 italic">No history found.</li>
              ) : (
                auditLog.map((log) => (
                  <li key={log.id} className="text-gray-700">
                    <strong>{log.user_name || 'Unknown'}</strong> – {log.action} –{' '}
                    {new Date(log.created_at).toLocaleString()}
                  </li>
                ))
              )}
            </ul>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setActiveStudentForAudit(null);
                  setAuditLog([]);
                }}
                className="bg-gray-200 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAP MODAL */}
      {mapModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded shadow w-[95%] max-w-2xl">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FaMapMarkerAlt /> Student Location{' '}
                {mapModal.name && <span className="text-gray-500">({mapModal.name})</span>}
              </h2>
              <button
                onClick={() =>
                  setMapModal({ open: false, lat: null, lon: null, name: '', address: '' })
                }
                className="text-red-500 px-3 py-1 rounded hover:bg-gray-100"
              >
                Close
              </button>
            </div>
            {mapModal.address && (
              <div className="mb-2 text-gray-700 text-xs italic pl-1">{mapModal.address}</div>
            )}
            <div className="w-full h-[400px] rounded">
              {mapModal.lat && mapModal.lon && (
                <MapContainer
                  center={[mapModal.lat, mapModal.lon]}
                  zoom={15}
                  scrollWheelZoom={false}
                  style={{ width: '100%', height: '400px', borderRadius: '12px' }}
                >
                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[mapModal.lat, mapModal.lon]} icon={studentIcon}>
                    <Popup>
                      {mapModal.name}
                      <br />
                      {mapModal.address}
                    </Popup>
                  </Marker>
                </MapContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Parent magic-login modal */}
      {parentLoginModalOpen && selectedStudentForParentLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow w-[95%] max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <FaKey className="text-amber-600" />
              Send parent login link
            </h3>
            <p className="text-sm mb-2">
              Student:{' '}
              <span className="font-semibold">
                {selectedStudentForParentLogin.last_name}
                {selectedStudentForParentLogin.last_name ? ', ' : ''}
                {selectedStudentForParentLogin.first_name}
              </span>
            </p>
            <p className="text-xs text-gray-600 mb-3">
              Select which parent should receive a one-time magic login link and QR code for the parent
              portal.
            </p>

            <ul className="space-y-2 mb-4">
              {(selectedStudentForParentLogin.parents || []).map((p, idx) => {
                const hasEmail = !!(p.email && p.email.trim() !== '');
                const canSend = hasEmail && p.id;
                return (
                  <li
                    key={p.id || `p-${idx}`}
                    className="border rounded px-3 py-2 flex items-center justify-between text-xs"
                  >
                    <div>
                      <div className="font-semibold">
                        {p.first_name} {p.last_name}{' '}
                        <span className="text-gray-500">({p.relation || 'parent'})</span>
                      </div>
                      <div className="text-gray-700">
                        {hasEmail ? p.email : <span className="italic">No email</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={!canSend || sendingParentId === p.id}
                      onClick={() => handleSendParentLoginLink(p)}
                      className={`ml-2 px-2 py-1 rounded flex items-center gap-1 ${
                        canSend
                          ? 'bg-amber-600 text-white hover:bg-amber-700'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <FaKey />
                      {sendingParentId === p.id ? 'Sending...' : 'Send'}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="flex justify-end gap-2">
              <button onClick={closeParentLoginModal} className="bg-gray-200 px-4 py-2 rounded text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageStudents;










